import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useFilter } from '@/application/use-filter'
import { useCountries } from '@/application/queries'
import { toCountryCodes } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[]
) {
  return [
    'dailyReturns',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections
  ]
}

export function useDailyReturns(startDate?: string, endDate?: string) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  function callDailyReturns() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getDailyReturns({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const dailyReturnsQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callDailyReturns,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const returnDays = dailyReturnsQuery.data?.map((ret) => ret.period) ?? []
  const totalItemsReturnedByDay =
    dailyReturnsQuery.data?.map((ret) => ret.totalReturnedItems ?? 0) ?? []

  return {
    returnDays,
    totalItemsReturnedByDay,
    dailyReturns: dailyReturnsQuery.data,
    loadingDailyReturns: dailyReturnsQuery.isLoading || loadingCountries
  }
}
